import React, { Component } from 'react'
import styled, { css } from 'styled-components'
import shortid from 'shortid'

import isNaN from 'lodash/isNaN'
import sum from 'ramda/src/sum'
import pluck from 'ramda/src/pluck'
import identity from 'ramda/src/identity'
import pipe from 'ramda/src/pipe'
import reverse from 'ramda/src/reverse'

import Tooltip, { withTooltip } from '../../lib/components/Tooltip'

import { maximum } from '../../lib/utils'

const fallbackNaN = (number) => (isNaN(number) ? '0.00' : number)

export const Root = styled.div`
  width: 570px;
  height: 14px;
  padding: 0 4px;

  position: relative;

  border-radius: 8px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;

  cursor: pointer;

  ${withTooltip}
`

/** @param {{ color?: string }} props */
const detailColor = props => props.color || 'inherit'

const Detail = styled.div`
  padding-top: 1px;

  display: flex;
  align-items: center;

  color: #333333;
  font-family: 'Gotham';
  font-size: 12px;
  line-height: 15px;

  &:not(:last-child) {
    margin-bottom: 6px;
  }

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    margin-right: 6px;

    background-color: ${detailColor};
    border-radius: 50%;
  }
`

const DetailLabel = styled.div`
  margin-right: 8px;
`

const DetailNumber = styled.div`
  margin-left: auto;
  font-family: 'NotoSans';
`

/** @param {{ position: number }} props */
const barPosition = props => props.position

/** @param {{ color?: string }} props */
const barColor = props => props.color || '#B2DDF7'

const Bar = styled.div`
  width: calc((100% - 8px) * ${barPosition});
  height: 6px;

  position: absolute;
  z-index: 300;
  top: 50%;
  left: 4px;

  border-radius: 6px;
  background-color: ${barColor};

  cursor: pointer;

  transform: translateY(-50%);
`

/** @param {{ position: number }} props */
const markerPosition = props => props.position

/** @param {{ type?: string }} props */
const markerType = props => props.type || 'solid'

/** @param {{ size?: string }} props */
const markerSize = props => props.size || '50px'

/** @param {{ color?: string }} props */
const markerColor = props => props.color || '#404040'

const markerNotch = css`
  &::before {
    content: '';
    width: 6px;
    height: 6px;

    position: absolute;
    top: 0;
    left: 0;
    transform: translate(-42%, -42%);

    border-radius: 50%;
    background-color: ${markerColor};
  }
`

const Marker = styled.div`
  height: ${markerSize};

  position: absolute;
  z-index: 350;
  bottom: -5px;
  left: calc((100% - 8px) * ${markerPosition} + 4px);

  border-right: thin ${markerType} ${markerColor};

  ${markerNotch}
`

/** @param {{ label?: string }} props */
const markerLabelContent = props => props.label

/** @param {{ direction: 'left' | 'right' }} props */
const markerLabelDirection = ({ direction = 'right' }) => direction

const MarkerLabel = styled.div`
  width: max-content;

  position: absolute;
  ${markerLabelDirection}: 5px;
  text-align: ${markerLabelDirection};

  color: #333;
  font-family: 'Gotham-Medium';
  font-size: 12px;
  line-height: 12px;

  &::before {
    content: '${markerLabelContent}';
    display: block;
    margin-bottom: 2px;

    color: #a1a1a1;
    font-family: 'Gotham';
    font-size: 10px;
    line-height: 10px;
  }
`

/**
 * @typedef {object} LabelFormatter
 * @property {(n: number) => string} [abbr]
 * @property {(n: number) => string} [full]
 *
 * @typedef {object} BarData
 * @property {number} value
 * @property {string} color
 * @property {string} [label]
 * @property {LabelFormatter} [formatter]
 *
 * @typedef {object} MarkerData
 * @property {number} value
 * @property {string} color
 * @property {string} [label]
 * @property {string} [type]
 * @property {'left' | 'right'} [direction]
 * @property {LabelFormatter} [formatter]
 * @property {(bars: BarData[], marker: MarkerData) => BarData[]} [trigger]
 */

/**
 * @typedef {object} Props
 * @property {boolean} hideLabel
 * @property {number} [threshold]
 * @property {BarData[]} bars
 * @property {MarkerData[]} [markers]
 */

/** @extends {Component<Props>} */
class IndicatorCost extends Component {
  render = () => {
    const { threshold = 0, bars, markers, hideLabel } = this.props

    const barTotal = sum(pluck('value', bars).map(x => (x <= 0 ? 0 : x)))
    const markerMax = maximum(pluck('value', markers))
    const total = barTotal + markerMax

    const enhancedBars = pipe(
      ...markers.map(marker => (marker.trigger ? xs => marker.trigger(xs, marker) : identity))
    )(bars)
    const details = enhancedBars.concat(markers)

    return (
      <Root>
        {reverse(enhancedBars).map(
          (bar, idx) =>
            bar.value > 0 && (
              <Bar
                threshold={ threshold }
                position={ (idx === 0 ? barTotal : bar.value) / total }
                color={ bar.color }
                key={ shortid.generate() }
              />
            )
        )}
        {markers.map(({ direction, color, value, type, label, formatter = { abbr: identity } }) => (
          <Marker
            key={ shortid.generate() }
            color={ color }
            size={ hideLabel ? '25px' : undefined }
            position={ value / total }
            type={ type }
          >
            <MarkerLabel
              direction={ direction }
              style={ { visibility: hideLabel ? 'hidden' : 'visible' } }
              label={ label }
            >
              {formatter.abbr(fallbackNaN(value))}
            </MarkerLabel>
          </Marker>
        ))}
        <Tooltip>
          {details.map(({ value, color, label, formatter = { full: identity } }) => (
            <Detail color={ color } key={ shortid.generate() }>
              <DetailLabel>{label}: </DetailLabel>
              <DetailNumber>{formatter.full(fallbackNaN(value))}</DetailNumber>
            </Detail>
          ))}
        </Tooltip>
      </Root>
    )
  }
}

export default IndicatorCost



// WEBPACK FOOTER //
// ./src/components/project-profit-sheet/IndicatorCost.jsx